import { Archive } from "lucide-react";
import { useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import type {
  BulkTransitionResult,
  EmployeeSummary,
  PageResponse,
  WorkItemStatus,
  WorkItemSummary,
  WorkItemTransition,
} from "../../shared/types/api";
import { Button, Field, InlineAlert, Input, Select, cn } from "../../shared/ui";
import { useQuery } from "@tanstack/react-query";
import { BatchReasonDrawer } from "./BatchReasonDrawer";
import { MoveToGroupDrawer } from "./MoveToGroupDrawer";
import { RequestChangesDrawer } from "./RequestChangesDrawer";
import { STATUS_LABELS } from "./workItemVocabulary";

/**
 * What can be done to a whole selection, and only what can be done to all of it.
 *
 * <p>A batch is one decision — "these six are ready for Divya" — so the bar offers a move only
 * when every selected item can make it. Offering a move that half the selection would refuse
 * turns one click into a report of failures, which is not what the person meant to do.
 */
const BATCH_MOVES: {
  transition: WorkItemTransition;
  label: string;
  from: WorkItemStatus;
  needsTester?: boolean;
  needsReasonPerItem?: boolean;
}[] = [
  { transition: "MARK_DEVELOPMENT_DONE", label: "Mark development done", from: "IN_PROGRESS" },
  {
    transition: "SEND_FOR_TESTING",
    label: "Send for testing",
    from: "DEV_DONE",
    needsTester: true,
  },
  { transition: "START_TESTING", label: "Start testing", from: "READY_FOR_QA" },
  { transition: "PASS_QA", label: "Pass QA", from: "QA" },
  /* Backwards, so it asks for a reason — and for one per item, because five things rarely fail
     for the same reason and a single code spread across them records four untruths. */
  { transition: "FAIL_QA", label: "Fail QA", from: "QA", needsReasonPerItem: true },
];

/**
 * Review actions, which are not moves.
 *
 * <p>Offered from any column, because review no longer runs on the board: work sent for testing
 * this morning can be reviewed this afternoon without leaving the tester's hands.
 */
const REVIEW_ACTIONS: { path: string; label: string; needsReviewer?: boolean }[] = [
  { path: "send", label: "Send for code review", needsReviewer: true },
  { path: "approve", label: "Approve review" },
];

export function BulkMoveBar({
  items,
  onDone,
  onClear,
}: {
  /** The selected rows themselves: the bar needs their statuses, keys and titles. */
  items: WorkItemSummary[];
  /** Told whether everything moved, so a partial failure keeps the selection and its message. */
  onDone: (allMoved: boolean) => void;
  onClear: () => void;
}) {
  const selected = items.map((item) => item.id);
  const statuses = items.map((item) => item.status);
  const [reviewerId, setReviewerId] = useState("");
  const [testerId, setTesterId] = useState("");
  const [testingDue, setTestingDue] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [results, setResults] = useState<BulkTransitionResult[] | null>(null);
  const [askingForChanges, setAskingForChanges] = useState(false);
  const [askingForReasons, setAskingForReasons] =
    useState<(typeof BATCH_MOVES)[number] | null>(null);
  const [filingAway, setFilingAway] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const distinct = [...new Set(statuses)];
  const available = BATCH_MOVES.filter(
    (move) => distinct.length === 1 && distinct[0] === move.from,
  );
  const needsTester = available.some((move) => move.needsTester);

  /*
   * Filing a batch away belongs to one project and to finished work only.
   *
   * <p>Offered from a different test than the moves above: those need one status, this needs
   * every status to be an ending — a tester clearing down after a release has both passed work
   * and cancelled work in front of them, and making them do it in two passes would be arbitrary.
   *
   * <p>One project, because a group lives on a project. The organisation-wide bug screen can
   * hold rows from three of them, and a batch spanning projects would have no group to go into.
   */
  const projects = [...new Set(items.map((item) => item.projectId))];
  const allFinished =
    items.length > 0 && statuses.every((one) => one === "DONE" || one === "CANCELLED");
  const canFileAway = allFinished && projects.length === 1;

  /* Named by whoever hands the work over, and changed by the testing team afterwards if they
     want somebody else — the same arrangement the reviewer has. */
  const people = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
  });

  const run = async (transition: WorkItemTransition) => {
    setBusy(transition);
    setError(null);
    setResults(null);
    try {
      /* Naming a tester before the move, so the refusal a missing one would cause never
         happens — the person sending the batch is the one who knows who should take it. */
      if (transition === "SEND_FOR_TESTING" && (testerId || testingDue)) {
        await Promise.all(selected.map((id) =>
          api.put(`/api/v1/work-items/${id}/testing`, {
            testerId: testerId || null,
            testingDueDate: testingDue || null,
          })));
      }
      const answered = await api.post<BulkTransitionResult[]>(
        "/api/v1/work-items/bulk-transition",
        {
          transition,
          workItemIds: selected,
          reviewerId: null,
          reason: null,
          reasonComment: null,
        },
      );
      const refused = answered.filter((row) => !row.moved);
      /* Everything moved is the ordinary case and needs no report; a refusal is the only thing
         worth reading, and it stays on screen until the selection changes. */
      setResults(refused.length > 0 ? refused : null);
      onDone(refused.length === 0);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not move those items.");
    } finally {
      setBusy(null);
    }
  };

  const review = async (path: string) => {
    setBusy(path);
    setError(null);
    setResults(null);
    try {
      const answered = await api.post<BulkTransitionResult[]>(
        `/api/v1/work-items/reviews/${path}`,
        { workItemIds: selected, reviewerId: reviewerId || null, comment: null },
      );
      const refused = answered.filter((row) => !row.moved);
      setResults(refused.length > 0 ? refused : null);
      onDone(refused.length === 0);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not do that to those items.");
    } finally {
      setBusy(null);
    }
  };

  if (selected.length === 0) {
    return null;
  }

  return (
    <>
    <div className="space-y-3 rounded-xl border border-brand-200 bg-brand-50/60 p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <span className="pb-2 text-sm font-medium text-ink-900">
            {selected.length} selected
          </span>
          {needsTester && (
            <>
              <Field label="Tester">
                <Select value={testerId} onChange={(event) => setTesterId(event.target.value)}>
                  <option value="">Leave unassigned</option>
                  {(people.data?.content ?? []).map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.fullName}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Testing due">
                <Input
                  type="date"
                  value={testingDue}
                  onChange={(event) => setTestingDue(event.target.value)}
                />
              </Field>
            </>
          )}
          {available.map((move) => (
            <Button
              key={move.transition}
              size="sm"
              onClick={() =>
                move.needsReasonPerItem
                  ? setAskingForReasons(move)
                  : void run(move.transition)
              }
              loading={busy === move.transition}
            >
              {move.label}
            </Button>
          ))}
          {/* The action a tester reaches for once a round of verification is over: take the
              finished work off the list, under a name, rather than scrolling past it forever. */}
          {canFileAway && (
            <Button
              size="sm"
              variant="secondary"
              icon={<Archive className="h-4 w-4" />}
              onClick={() => setFilingAway(true)}
            >
              Move to a group…
            </Button>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onClear}>
          Clear selection
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3 border-t border-brand-200/70 pt-3">
        <span className="pb-2 text-sm text-ink-600">Code review</span>
        <Field label="Reviewer">
          <Select value={reviewerId} onChange={(event) => setReviewerId(event.target.value)}>
            <option value="">Nobody yet</option>
            {(people.data?.content ?? []).map((person) => (
              <option key={person.id} value={person.id}>
                {person.fullName}
              </option>
            ))}
          </Select>
        </Field>
        {REVIEW_ACTIONS.map((action) => (
          <Button
            key={action.path}
            size="sm"
            variant="secondary"
            onClick={() => void review(action.path)}
            loading={busy === action.path}
          >
            {action.label}
          </Button>
        ))}
        {/* Sending work back is the one review decision that needs words, so it asks for them
            rather than firing on the click. */}
        <Button size="sm" variant="secondary" onClick={() => setAskingForChanges(true)}>
          Request changes
        </Button>
      </div>

      {available.length === 0 && !canFileAway && (
        <p className="text-sm text-ink-600">
          {distinct.length > 1
            ? "Those are in different columns. A batch moves together, so select items that are all at the same stage."
            : `Nothing can be done to a batch from ${STATUS_LABELS[distinct[0]!]}.`}
        </p>
      )}

      {error && <InlineAlert tone="error">{error}</InlineAlert>}

      {results && results.length > 0 && (
        <InlineAlert tone="warning">
          <div className="space-y-1">
            <p>{results.length} did not move. The rest did.</p>
            {results.map((row) => (
              <p key={row.workItemId} className={cn("text-sm")}>
                <span className="font-medium">{row.itemKey ?? "Item"}</span> — {row.refusedBecause}
              </p>
            ))}
          </div>
        </InlineAlert>
      )}
    </div>

    {askingForReasons && (
      <BatchReasonDrawer
        items={items}
        transition={askingForReasons.transition}
        label={askingForReasons.label}
        open
        onClose={() => setAskingForReasons(null)}
        onDone={onDone}
      />
    )}

    {canFileAway && (
      <MoveToGroupDrawer
        projectId={projects[0]!}
        items={items}
        open={filingAway}
        onClose={() => setFilingAway(false)}
        /* Treated as a clean batch: the rows have left every list, so the selection is stale. */
        onDone={() => onDone(true)}
      />
    )}

    <RequestChangesDrawer
      items={items}
      open={askingForChanges}
      onClose={() => setAskingForChanges(false)}
      onDone={onDone}
    />
    </>
  );
}
